import React, { Suspense, lazy, useEffect, useMemo, useState } from "react"
import { Box, InputAdornment, TextField, Typography } from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import { materialIconLoaders } from "./materialIconsLoaders"

const PAGE_SIZE = 60
const SCROLL_LOAD_THRESHOLD_PX = 48

const iconComponentCache = {}

// Loads a single @mui/icons-material component by name, on demand — searching the ~2100
// icon index must not pull the whole icon package into the bundle.
// materialIconsLoaders.js (generated by client/app/scripts/generateMaterialIconsIndex.js)
// gives each icon its own static `import()` literal: webpack's context-module resolution
// for an expression import into a node_modules package (`import(`@mui/icons-material/${name}`)`)
// proved unreliable — empty map at runtime, "Cannot find module" regardless of extension.
export const getMaterialIconComponent = (name) => {
  if (!iconComponentCache[name]) {
    iconComponentCache[name] = lazy(materialIconLoaders[name])
  }
  return iconComponentCache[name]
}

const sx = {
  root: {
    width: 280,
    boxSizing: "border-box",
  },
  search: {
    px: "12px",
    pt: "12px",
    pb: "8px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: "4px",
    px: "12px",
    pb: "12px",
    maxHeight: 260,
    overflowY: "auto",
  },
  cell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: "8px",
    border: "none",
    bgcolor: "transparent",
    color: "#414141",
    cursor: "pointer",
    p: 0,
    "&:hover": { bgcolor: "#F0F0F0" },
  },
  empty: {
    gridColumn: "1 / -1",
    textAlign: "center",
    color: "#9C9C9C",
    fontSize: 13,
    py: "16px",
  },
}

const EnterpriseLogoIconPicker = ({ onSelect }) => {
  const [search, setSearch] = useState("")
  const [icons, setIcons] = useState(null)
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  useEffect(() => {
    let dead = false
    import("./materialIconsIndex.json").then((mod) => {
      if (!dead) setIcons(mod.default)
    })
    return () => { dead = true }
  }, [])

  const filteredIcons = useMemo(() => {
    if (!icons) return []
    const term = search.trim().toLowerCase()
    if (!term) return icons
    return icons.filter(({ keywords }) => keywords.some((keyword) => keyword.includes(term)))
  }, [icons, search])

  // Infinite scroll: reset to the first page whenever the search term changes the result set
  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [search])

  const results = filteredIcons.slice(0, visibleCount)
  const hasMore = visibleCount < filteredIcons.length

  const handleGridScroll = (e) => {
    if (!hasMore) return
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget
    if (scrollHeight - (scrollTop + clientHeight) < SCROLL_LOAD_THRESHOLD_PX) {
      setVisibleCount((v) => Math.min(v + PAGE_SIZE, filteredIcons.length))
    }
  }

  return (
    <Box sx={sx.root}>
      <Box sx={sx.search}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Rechercher une icône"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={sx.grid} onScroll={handleGridScroll}>
        {results.map(({ name }) => {
          const IconComponent = getMaterialIconComponent(name)
          return (
            <Box
              key={name}
              component="button"
              type="button"
              sx={sx.cell}
              title={name}
              aria-label={name}
              onClick={() => onSelect(name)}
            >
              <Suspense fallback={null}>
                <IconComponent fontSize="small" />
              </Suspense>
            </Box>
          )
        })}
        {icons && results.length === 0 && (
          <Typography sx={sx.empty}>Aucune icône trouvée</Typography>
        )}
      </Box>
    </Box>
  )
}

export default EnterpriseLogoIconPicker
